import init, {
  analyze as wasmAnalyze,
  prettify as wasmPrettify,
  tokenize_packed as wasmTokenizePacked,
} from '../../pkg/simple_fiddle.js';
import { compilePreviewDocument, buildStandaloneDocument } from './document-builder.js';
import { getPreviewThemeCss } from './theme-tokens.js';

// Above this size, Rust-side formatting/validation is skipped rather than
// run: the formatters and validator are otherwise safe (see the R-1/R-2
// fixes in src/formatter and src/validator), but there is no reason to spend
// the time on a buffer this large from a single keypress or click. Mirrors
// the 240 KB "idle highlighting" threshold in features/live-reflection.js.
export const LARGE_SOURCE_BYTES = 240 * 1024;

let isInitialized = false;
const analysisCache = new Map();
let analysisWorker = null;
let workerRequestId = 0;
const workerRequests = new Map();

const TOKEN_TYPES = [
  '',
  'tag',
  'attr',
  'string',
  'comment',
  'text',
  'selector',
  'property',
  'value',
  'punctuation',
  'keyword',
  'identifier',
  'number',
  'operator'
];
const WORKER_TIMEOUT_MS = 2000;

function normalizedLang(lang) {
  return String(lang || '').trim();
}

function decodePackedTokens(packed) {
  if (!packed || typeof packed.length !== 'number' || packed.length % 3 !== 0) return null;

  const tokens = [];
  for (let index = 0; index < packed.length; index += 3) {
    const type = TOKEN_TYPES[packed[index]];
    if (!type) continue;
    tokens.push({
      type,
      start: packed[index + 1],
      len: packed[index + 2]
    });
  }
  return tokens;
}

function packedTokenRecord(code, lang) {
  const key = normalizedLang(lang);
  const source = String(code ?? '');
  const cached = analysisCache.get(key);
  if (cached && cached.code === source && cached.tokens) return cached;

  const tokens = decodePackedTokens(wasmTokenizePacked(source, key));
  if (!tokens) return analysisRecord(source, key);

  const next = {
    code: source,
    tokens,
    warnings: cached && cached.code === source ? cached.warnings : [],
    warningsLoaded: cached && cached.code === source ? Boolean(cached.warningsLoaded) : false,
    tokensJson: JSON.stringify(tokens)
  };
  analysisCache.set(key, next);
  return next;
}

function analysisRecord(code, lang) {
  const key = normalizedLang(lang);
  const source = String(code ?? '');
  const cached = analysisCache.get(key);
  if (cached && cached.code === source && cached.warningsLoaded) return cached;

  const analysis = JSON.parse(wasmAnalyze(source, key));
  const tokens = Array.isArray(analysis?.tokens) ? analysis.tokens : [];
  const warnings = Array.isArray(analysis?.warnings) ? analysis.warnings : [];
  const next = {
    code: source,
    tokens,
    warnings,
    warningsLoaded: true,
    tokensJson: JSON.stringify(tokens)
  };
  analysisCache.set(key, next);
  return next;
}

function ensureAnalysisWorker() {
  if (analysisWorker || typeof Worker === 'undefined') return analysisWorker;

  try {
    analysisWorker = new Worker(new URL('./wasm-worker.js', import.meta.url), { type: 'module' });
  } catch (error) {
    console.warn('[Front-my-End] WASM analysis worker unavailable; using main-thread scanner.', error);
    analysisWorker = null;
    return null;
  }

  analysisWorker.addEventListener('message', (event) => {
    const { id, error, tokens } = event.data || {};
    const request = workerRequests.get(id);
    if (!request) return;
    workerRequests.delete(id);
    clearTimeout(request.timer);
    if (error) {
      request.reject(new Error(error));
    } else {
      request.resolve(tokens);
    }
  });

  analysisWorker.addEventListener('error', (event) => {
    const pending = Array.from(workerRequests.values());
    workerRequests.clear();
    analysisWorker?.terminate();
    analysisWorker = null;
    for (const request of pending) {
      clearTimeout(request.timer);
      request.reject(event.error || new Error('WASM analysis worker failed.'));
    }
  });

  return analysisWorker;
}

function requestWorkerTokens(code, lang) {
  const worker = ensureAnalysisWorker();
  if (!worker) return Promise.reject(new Error('WASM analysis worker unavailable.'));

  const id = ++workerRequestId;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      workerRequests.delete(id);
      reject(new Error('WASM analysis worker timed out.'));
    }, WORKER_TIMEOUT_MS);
    workerRequests.set(id, { resolve, reject, timer });
    worker.postMessage({ id, type: 'tokenize', code, lang });
  });
}

function cacheTokens(code, lang, tokens) {
  const key = normalizedLang(lang);
  const source = String(code ?? '');
  const cached = analysisCache.get(key);
  const next = {
    code: source,
    tokens,
    warnings: cached && cached.code === source ? cached.warnings : [],
    warningsLoaded: cached && cached.code === source ? Boolean(cached.warningsLoaded) : false,
    tokensJson: JSON.stringify(tokens)
  };
  analysisCache.set(key, next);
  return next;
}

/**
 * Initializes the precompiled WebAssembly engine.
 * Ensures single instantiation.
 */
export async function initWasm() {
  if (isInitialized) return;
  await init();
  isInitialized = true;
}

/**
 * Compiles HTML, CSS, JS, lazy-loaded theme tokens, and preview route state into a single preview document.
 */
export async function compileCode(html, css, js, theme, activeRoute = '', activePath = 'index.html', options = {}) {
  const themeCss = await getPreviewThemeCss(theme);
  return compileCodeWithThemeCss(html, css, js, themeCss, activeRoute, activePath, options);
}

export function compileCodeWithThemeCss(html, css, js, themeCss, activeRoute = '', activePath = 'index.html', options = {}) {
  return compilePreviewDocument(html, css, js, themeCss, activeRoute || '', activePath || 'index.html', options);
}

/**
 * Generates a standalone single-file HTML document for export.
 */
export function exportCode(html, css, js) {
  return buildStandaloneDocument(html, css, js);
}

/**
 * Invokes the Rust WASM tokenizer to parse source code into tokens.
 */
export function tokenizeCode(code, lang) {
  if (!isInitialized) return '[]';
  try {
    return packedTokenRecord(code, lang).tokensJson;
  } catch (e) {
    console.error(`Failed to tokenize ${lang} source:`, e);
    return '[]';
  }
}

export function tokenizeCodeParsed(code, lang) {
  if (!isInitialized) return [];
  try {
    return packedTokenRecord(code, lang).tokens;
  } catch (e) {
    console.error(`Failed to parse ${lang} tokens:`, e);
    return [];
  }
}

export async function tokenizeCodeParsedAsync(code, lang) {
  if (!isInitialized) return [];
  const key = normalizedLang(lang);
  const source = String(code ?? '');
  const cached = analysisCache.get(key);
  if (cached && cached.code === source && cached.tokens) return cached.tokens;

  try {
    const packed = await requestWorkerTokens(source, key);
    const tokens = decodePackedTokens(packed) || [];
    cacheTokens(source, key, tokens);
    return tokens;
  } catch (_error) {
    return tokenizeCodeParsed(source, key);
  }
}

/**
 * Formats HTML, CSS, and JS using the offline-first Rust WASM engine.
 */
export function prettifyCode(code, lang) {
  if (!isInitialized) return code;
  return wasmPrettify(code, lang);
}

/**
 * Runs lightweight Rust-side syntax checks for editor diagnostics.
 */
export function validateCode(code, lang) {
  if (!isInitialized) return [];
  try {
    return analysisRecord(code, lang).warnings;
  } catch (e) {
    console.error(`Failed to validate ${lang} source:`, e);
    return [];
  }
}

// Sandbox origin policy lives in a dependency-free module so the production
// fail-closed behavior can be unit-tested without the WASM build. Re-exported
// here to keep the existing `core/wasm.js` import path stable for callers.
export { getSandboxUrl } from './sandbox-origin.js';
